//NEW: UI_REVAMP
import { useDisclosure } from '@chakra-ui/react';
import { useMutation, useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useCallback, useEffect, useState } from 'react';
import { getSrvTimeApi } from '../api/query/authApi';
import {
	getCutOffTimeApi,
	getDrawApi,
	getGameApi,
	getNumberLimitApi,
	getOddsApi,
	postPostApi,
} from '../api/query/betApi';
import { getCreditRemainApi } from '../api/query/profileApi';
import { BETTING_TABS } from '../components/betting/BettingControls';
import BettingDesktopView from '../components/betting/BettingDesktopView';
import BettingMobileView from '../components/betting/BettingMobileView';
import GlassModal from '../components/modal/GlassModal';
import { useBottomSheetStore } from '../hooks/useBottomSheetStore';
import { useBettingScreen, useCheckingStore, useGameStore } from '../hooks/useStore';
import { useBet1Store } from '../stores/useBet1Store';
import { useBet3Store } from '../stores/useBet3Store';
import { useBetTwo2DStore } from '../stores/useBetTwo2DStore';
import { useBetTwo3DStore } from '../stores/useBetTwo3DStore';
import { getMinBetAmount } from '../utils/getMinBetAmount';
import { currencyFormat } from '../utils/numberFormat';
import { useGlobalToast } from '../utils/ShowToast';
import ServerTime from '../utils/Time';

const KeyboardScreen = ({ siteId: routeSiteId = 1 }) => {
	//global
	const globalShowToast = useGlobalToast();
	const { serverTime } = ServerTime();

	//modal
	const {
		isOpen: isOpenCheckout,
		onOpen: onOpenCheckout,
		onClose: onCloseCheckout,
	} = useDisclosure();

	//dayjs
	var utc = require('dayjs/plugin/utc');
	var timezone = require('dayjs/plugin/timezone');
	dayjs.extend(utc);
	dayjs.extend(timezone);

	//zustand
	const betting = useBettingScreen((state) => state.betting);
	const setResults = useBettingScreen((state) => state.setResults);
	const clearResults = useBettingScreen((state) => state.clearResults);
	const setBetting = useBettingScreen((state) => state.setBetting);
	const removeBetting = useBettingScreen((state) => state.removeBetting);
	const removeAll = useBettingScreen((state) => state.removeAll);
	const closeBottomSheet = useBottomSheetStore((state) => state.closeBottomSheet);
	const siteId = useGameStore((state) => state.siteId);
	const setSiteId = useGameStore((state) => state.setSiteId);
	const setCheckPost = useCheckingStore((state) => state.setCheckPost);
	const {
		swer3Selection,
		ez2Selection,
		updateSwer3Selection,
		updateEz2Selection,
		setSwer3Selection,
		setEz2Selection,
		clearSelections,
	} = useBet1Store();
	const bet2EzSlots = useBetTwo2DStore((state) => state.slots);
	const bet2SwerSelected = useBetTwo3DStore((state) => state.selectedNumbers);
	const setBet2EZLimit = useBetTwo2DStore((state) => state.setNumberLimit);
	const setBet2SwerLimit = useBetTwo3DStore((state) => state.setNumberLimit);
	const bet3Slots = useBet3Store((state) => state.slots);

	//state
	const [gameId, setGameId] = useState(1);
	const [timeBtnId, setTimeBtnId] = useState();
	const [time, setTime] = useState('');
	const [timeName, setTimeName] = useState('');
	const [activeTimeBtn, setActiveTimeBtn] = useState(false);
	const [hasServerTime, setHasServerTime] = useState(false);
	const [activeBetTab, setActiveBetTab] = useState(BETTING_TABS.bet1.value);
	const [activeSiteType, setActiveSiteType] = useState(0);
	const [activeBetType, setActiveBetType] = useState(null);
	const [showBetList, setShowBetList] = useState(true);
	const [isLuckyPick, setIsLuckyPick] = useState(false);
	const [invalidBetIds, setInvalidBetIds] = useState([]);

	//& api
	const getSrvTime = useQuery(['getSrvTimeApi'], getSrvTimeApi);
	const currentSiteId = routeSiteId || siteId;
	const getGame = useQuery(['getGameApi', currentSiteId], () => getGameApi(Number(currentSiteId)));
	const getDraw = useQuery(['getDrawApi', gameId], () => getDrawApi(gameId));
	const getCreditRemain = useQuery(['getCreditRemainApi'], getCreditRemainApi);
	const getCutOffTime = useQuery(['getCutOffTimeApi'], getCutOffTimeApi);
	const getOdds = useQuery(['getOddsApi'], getOddsApi);
	const getNumberLimit = useQuery(['getNumberLimitApi', currentSiteId], () =>
		getNumberLimitApi(Number(currentSiteId)),
	);

	//mutation
	const postPost = useMutation({
		mutationFn: (payload) => postPostApi(payload),
		onSuccess: (succ) => {
			const failedBets = succ?.betting?.filter((b) => b.isFail) || [];
			const hasSuccess = succ?.betting?.some((b) => !b.isFail);

			if (failedBets.length > 0) {
				failedBets.forEach((b) => {
					globalShowToast({
						title: b.message || 'Bet failed',
						status: 'error',
						position: 'top',
					});
				});
				onCloseCheckout();
				clearBettingInput();
				removeAll();
				closeBottomSheet();
			}

			if (hasSuccess) {
				onCloseCheckout();
				clearBettingInput();
				removeAll();
				closeBottomSheet();
				setResults(succ);
			} else {
				setCheckPost(false);
			}
			if (succ?.ticketId) {
				sessionStorage.setItem('accessTicketId', succ.ticketId);
			}
			setCheckPost(false);
			getCreditRemain.refetch();
			getNumberLimit.refetch();
		},
		onError: (err) => {
			onCloseCheckout();
			globalShowToast({
				title: err.response.data.data.message,
				status: 'error',
				position: 'top',
			});
			setCheckPost(false);
		},
	});

	//derived states
	const activeGame = getGame.data?.[activeSiteType]?.name;
	const is3DGame = ['SWER3', 'MANDAUE', 'RG7'].includes(activeGame);
	const is2DGame = ['EZ2'].includes(activeGame);
	const isDisabled = time === '';

	const isSelectionIncomplete = (() => {
		if (activeBetType === null) return true;
		if (activeBetTab === 'bet1') {
			if (is3DGame) {
				return swer3Selection.some((col) => col.length === 0);
			} else if (is2DGame) {
				return ez2Selection.some((col) => col.length === 0);
			}
		} else if (activeBetTab === 'bet2') {
			if (activeSiteType === 0) {
				// Bet 2 3D
				return bet2SwerSelected.length === 0;
			} else if (activeSiteType === 1) {
				// Bet 2 2D
				return !bet2EzSlots[0] || !bet2EzSlots[1];
			}
		} else if (activeBetTab === 'bet3') {
			if (activeSiteType === 0) {
				// Bet 3 3D
				return bet3Slots.length < 3 || bet3Slots.some((s) => s === '');
			} else if (activeSiteType === 1) {
				// Bet 3 2D
				return bet3Slots.length < 2 || bet3Slots.some((s) => s === '');
			}
		}
		return false;
	})();

	//helpers
	const cartesian = (...args) =>
		args.reduce((a, b) => a.flatMap((d) => b.map((e) => [d, e].flat())));

	const removeAllByDigit = (digit) => {
		const betsToRemove = useBettingScreen.getState().betting.filter((b) => b.digit === digit);
		betsToRemove.forEach((b) => removeBetting(b.id));
	};

	const isInvalidRamble = (digit) => {
		if (!activeBetType) return false;
		if (is3DGame) {
			return digit.length === 3 && digit[0] === digit[1] && digit[1] === digit[2];
		} else if (is2DGame) {
			const parts = digit.split(' ');
			return parts.length === 2 && parts[0] === parts[1];
		}
		return false;
	};

	const checkAndToastInvalidRamble = (digit, gameType) => {
		if (isInvalidRamble(digit, gameType)) {
			globalShowToast({
				title: 'Ramble bet cannot contain all same digits.',
				status: 'error',
				position: 'top',
			});
			return true;
		}
		return false;
	};

	const checkAndToastMax31 = (digit) => {
		if (is2DGame) {
			const parts = digit.split(' ');
			if (parts.some((p) => p !== '' && (parseInt(p) < 1 || parseInt(p) > 31))) {
				globalShowToast({
					title: 'Number Input 1 - 31 only.',
					status: 'error',
					position: 'top',
				});
				return true;
			}
		}
		return false;
	};

	const clearBettingInput = () => {
		clearSelections();
		setIsLuckyPick(false);
	};

	const checkDrawTime = () => {
		if (!getDraw.data) return;

		const nowMYT = dayjs(serverTime).tz('Asia/Kuala_Lumpur');

		const drawSelect = getDraw.data.find((draw) => {
			if (draw.cancelled) return false;

			const drawTime = dayjs.utc(draw.drawTime).tz('Asia/Kuala_Lumpur');

			return nowMYT.isBefore(drawTime);
		});

		if (drawSelect) {
			const drawTimeMYT = dayjs.utc(drawSelect.drawTime).tz('Asia/Kuala_Lumpur');

			setTime(drawTimeMYT.format('h:mm A'));
			setTimeBtnId(drawSelect.id);
			setTimeName(drawSelect.name);
		} else {
			if (time !== '') {
				globalShowToast({
					title: 'Draw time ended.',
					status: 'error',
					position: 'top',
				});
			}

			setTime('');
			setTimeBtnId(null);
			setTimeName('');
		}
	};

	//ui handlers
	const handleTabChange = (tab) => {
		clearResults();
		setActiveBetTab(tab);
		setActiveSiteType(0);
		setActiveBetType(null);
		setInvalidBetIds([]);

		clearSelections();
		useBetTwo2DStore.getState().clearSlots();
		useBetTwo3DStore.getState().clearSelectedNumbers();

		const defaultSlotCount = tab === 'bet3' ? 3 : 2;
		useBet3Store.getState().clearSlots(defaultSlotCount);
	};

	const handleTypeChange = (type) => {
		clearResults();
		setActiveSiteType(type);
	};

	const handleTimeChange = (id) => {
		clearResults();
		setTimeBtnId(id);
	};

	// checkout/submit
	const handleOpenCheckout = () => {
		const validBets = betting.filter((bet) => bet.drawId === timeBtnId);
		const totalBetPrice = validBets.reduce((sum, bet) => sum + bet.betPrice, 0);

		const currentDraw = getDraw.data?.find((d) => d.id === timeBtnId);
		const nowMYT = dayjs(serverTime).tz('Asia/Kuala_Lumpur');
		const drawTimeMYT = currentDraw
			? dayjs.utc(currentDraw.drawTime).tz('Asia/Kuala_Lumpur')
			: null;
		const isDrawExpired = !drawTimeMYT || nowMYT.isAfter(drawTimeMYT) || currentDraw?.cancelled;

		if (isDrawExpired) {
			globalShowToast({
				title: 'No valid bets for the current draw time.',
				status: 'warning',
				position: 'top',
			});
			return;
		}

		if (validBets.length === 0) {
			globalShowToast({
				title: 'Number input is empty',
				status: 'warning',
				position: 'top',
			});
			return;
		}

		const soldOutBets = validBets.filter((bet) => {
			const betTypeLabel = bet.betType;
			const gameLimitData = getNumberLimit.data?.find((d) => d.gameName === bet.gameType);
			const drawLimitData = gameLimitData?.draws?.find((d) => (d.id || d.drawId) === timeBtnId);
			const typeLimit = drawLimitData?.list?.find((l) => l.betType === betTypeLabel);
			const currentSoldOutStr = typeLimit?.soldOut || [];
			return currentSoldOutStr.includes(bet.digit);
		});

		if (soldOutBets.length === validBets.length) {
			soldOutBets.forEach((b) => {
				globalShowToast({
					title: `Bet failed, number ${b.digit} is sold out`,
					status: 'error',
					position: 'top',
				});
			});
			return;
		}

		if (totalBetPrice > getCreditRemain.data?.creditRemain) {
			globalShowToast({
				title: `Low Balance, Current Balance: PHP ${currencyFormat(
					getCreditRemain.data?.creditRemain,
				)}`,
				status: 'error',
				position: 'top',
			});
			return;
		}

		const betsBelowMin = validBets.filter(
			(bet) => bet.betPrice < getMinBetAmount(getGame, bet.gameId),
		);
		if (betsBelowMin.length > 0) {
			setInvalidBetIds(betsBelowMin.map((b) => b.id));

			const straightBelow = betsBelowMin.find((b) => b.betType === 'Straight');
			const rambleBelow = betsBelowMin.find((b) => b.betType === 'Ramble');

			if (straightBelow) {
				globalShowToast({
					title: `Straight minimum bet amount is ${getMinBetAmount(getGame, straightBelow.gameId)} PHP`,
					status: 'error',
					position: 'top',
				});
			}
			if (rambleBelow) {
				globalShowToast({
					title: `Ramble minimum bet amount is ${getMinBetAmount(getGame, rambleBelow.gameId)} PHP`,
					status: 'error',
					position: 'top',
				});
			}
			return;
		}

		setInvalidBetIds([]);
		onOpenCheckout();
	};

	const handleSubmit = () => {
		const validBets = betting.filter((bet) => bet.drawId === timeBtnId);
		const totalBetPrice = validBets.reduce((sum, bet) => sum + bet.betPrice, 0);

		if (totalBetPrice <= getCreditRemain.data?.creditRemain) {
			setCheckPost(true);

			const transformedBets = validBets.map((bet) => {
				const isRamble = bet.betType === 'Ramble';
				const is3D = ['SWER3', 'MANDAUE', 'RG7'].includes(bet.gameType);

				const getOddsValue = () => {
					if (!bet.odds || bet.odds.length === 0) return 0;
					if (isRamble) {
						if (is3D) {
							const uniqueCount = new Set(bet.digit.split('')).size;
							const permutation = uniqueCount === 3 ? 6 : uniqueCount === 2 ? 3 : 1;
							return bet.odds.find((o) => o.comboCount === permutation)?.winRate ?? 0;
						}
						return bet.odds.find((o) => o.comboCount > 1)?.winRate ?? 0;
					}
					return bet.odds.find((o) => o.comboCount === 1)?.winRate ?? 0;
				};

				const formatNumberArray = () => {
					if (is3D) {
						return bet.digit.split('').map((d, i) => ({
							digit: d,
							lp: bet.isLuckyPick,
							position: i + 1,
							name: `number3DNum${i + 1}`,
						}));
					} else if (bet.gameType === 'EZ2') {
						const pairs = bet.digit.split(' ');
						return pairs.map((d, i) => ({
							digit: d,
							lp: bet.isLuckyPick,
							position: i + 1,
							name: `number2DNum${i + 1}`,
						}));
					}
					return [];
				};

				return {
					gameId: bet.gameId,
					gameType: bet.gameType,
					betPrice: bet.betPrice,
					ramble: isRamble,
					odds: getOddsValue(),
					lp: bet.isLuckyPick,
					number: formatNumberArray(),
				};
			});

			postPost.mutate({
				drawId: timeBtnId,
				totalBetPrice: totalBetPrice,
				betting: transformedBets,
			});
		} else {
			onCloseCheckout();
			globalShowToast({
				title: `Low Balance, Current Balance: PHP ${currencyFormat(
					getCreditRemain.data?.creditRemain,
				)}`,
				status: 'error',
				position: 'top',
			});
		}
	};

	// betting actions
	const handleLucky = () => {
		if (activeBetType === null) {
			globalShowToast({
				title: 'Please select Straight or Ramble',
				status: 'warning',
				position: 'top',
			});
			return;
		}
		clearResults();
		setIsLuckyPick(true);

		const betTypeLabel = activeBetType ? 'Ramble' : 'Straight';
		const gameLimitData = getNumberLimit.data?.find((d) => d.gameName === activeGame);
		const drawLimitData = gameLimitData?.draws?.find((d) => (d.id || d.drawId) === timeBtnId);
		const typeLimit = drawLimitData?.list?.find((l) => l.betType === betTypeLabel);
		const currentSoldOutStr = typeLimit?.soldOut || [];
		const isNumberSoldOut = (str) => currentSoldOutStr.includes(str);

		let digitStr = '';
		do {
			if (is3DGame) {
				const n1 = Math.floor(Math.random() * 10);
				const n2 = Math.floor(Math.random() * 10);
				const n3 = Math.floor(Math.random() * 10);
				digitStr = `${n1}${n2}${n3}`;
			} else if (is2DGame) {
				const n1 = Math.floor(Math.random() * 31) + 1;
				const n2 = Math.floor(Math.random() * 31) + 1;
				digitStr = `${n1.toString().padStart(2, '0')} ${n2.toString().padStart(2, '0')}`;
			}
		} while (isNumberSoldOut(digitStr) || (activeBetType && isInvalidRamble(digitStr)));

		const newBet = {
			id: `${Date.now()}-${Math.random()}`,
			gameId: gameId,
			gameType: activeGame,
			digit: digitStr,
			betType: betTypeLabel,
			betPrice: getMinBetAmount(getGame, gameId) || 1,
			timeName,
			drawId: timeBtnId,
			betTab: activeBetTab,
			odds: getOdds.data?.filter((item) => item.gameId === gameId) || [],
			isLuckyPick: true,
		};

		setBetting([newBet]);
		setIsLuckyPick(false);
	};

	const handleDigitClick = useCallback(
		(colIdx, digit) => {
			if (activeBetType === null) {
				globalShowToast({
					title: 'Please select Straight or Ramble',
					status: 'warning',
					position: 'top',
				});
				return;
			}
			clearResults();
			setIsLuckyPick(false);

			if (is3DGame) {
				updateSwer3Selection(colIdx, digit);
			} else if (is2DGame) {
				updateEz2Selection(colIdx, digit);
			}
		},
		[updateSwer3Selection, updateEz2Selection, is3DGame, is2DGame, activeBetType],
	);

	const handleSwer3Add = (lucky) => {
		const isLucky = typeof lucky === 'boolean' ? lucky : isLuckyPick;
		if (is3DGame) {
			const currentSwer3Selection = useBet1Store.getState().swer3Selection;
			if (currentSwer3Selection.every((col) => col.length > 0)) {
				let combinations = cartesian(...currentSwer3Selection).map((combo) => combo.join(''));

				if (activeBetType) {
					const totalCount = combinations.length;
					combinations = combinations.filter((digit) => !isInvalidRamble(digit, activeGame));
					if (combinations.length < totalCount) {
						globalShowToast({
							title: 'Ramble bet cannot contain all same digits.',
							status: 'error',
							position: 'top',
						});
					}
				}

				const newBets = combinations.map((digit) => ({
					id: `${Date.now()}-${Math.random()}`,
					gameId: gameId,
					gameType: activeGame,
					digit,
					betType: activeBetType ? 'Ramble' : 'Straight',
					betPrice: getMinBetAmount(getGame, gameId) || 1,
					timeName,
					drawId: timeBtnId,
					betTab: activeBetTab,
					odds: getOdds.data?.filter((item) => item.gameId === gameId) || [],
					isLuckyPick: isLucky,
				}));

				if (newBets.length > 0) {
					clearResults();
					setBetting(newBets);
					setSwer3Selection([[], [], []]);
					setIsLuckyPick(false);
				}
			} else {
				globalShowToast({
					title: 'Please pick one number per column',
					status: 'warning',
					position: 'top',
				});
			}
		}
	};

	const handleEz2Add = (lucky) => {
		const isLucky = typeof lucky === 'boolean' ? lucky : isLuckyPick;
		if (is2DGame) {
			const currentEz2Selection = useBet1Store.getState().ez2Selection;
			if (currentEz2Selection.every((col) => col.length > 0)) {
				const col1 = currentEz2Selection[0];
				const col2 = currentEz2Selection[1];
				const col3 = currentEz2Selection[2];
				const col4 = currentEz2Selection[3];
				const set1All = cartesian(col1, col2).map((c) => c.join(''));
				const set2All = cartesian(col3, col4).map((c) => c.join(''));

				const isRangeValid = (val) => {
					const v = parseInt(val);
					return v >= 1 && v <= 31;
				};

				const set1Valid = set1All.filter(isRangeValid);
				const set2Valid = set2All.filter(isRangeValid);

				if (set1Valid.length !== set1All.length || set2Valid.length !== set2All.length) {
					globalShowToast({
						title: 'Number Input 01 - 31 only.',
						status: 'error',
						position: 'top',
					});
				}

				if (set1Valid.length === 0 || set2Valid.length === 0) return;

				let finalCombinations = cartesian(set1Valid, set2Valid).map((c) => c.join(' '));

				if (activeBetType) {
					const totalCount = finalCombinations.length;
					finalCombinations = finalCombinations.filter(
						(digit) => !isInvalidRamble(digit, activeGame),
					);
					if (finalCombinations.length < totalCount) {
						globalShowToast({
							title: 'Ramble bet cannot contain all same digits.',
							status: 'error',
							position: 'top',
						});
					}
				}

				const newBets = finalCombinations.map((digit) => ({
					id: `${Date.now()}-${Math.random()}`,
					gameId: gameId,
					gameType: activeGame,
					digit,
					betType: activeBetType ? 'Ramble' : 'Straight',
					betPrice: getMinBetAmount(getGame, gameId) || 1,
					timeName,
					drawId: timeBtnId,
					betTab: activeBetTab,
					odds: getOdds.data?.filter((item) => item.gameId === gameId) || [],
					isLuckyPick: isLucky,
				}));

				if (newBets.length > 0) {
					clearResults();
					setBetting(newBets);
					setEz2Selection([[], [], [], []]);
					setIsLuckyPick(false);
				}
			} else {
				globalShowToast({
					title: 'Please pick one number per column',
					status: 'warning',
					position: 'top',
				});
			}
		}
	};

	const handleBet2SwerAdd = (lucky) => {
		if (activeBetType === null) {
			globalShowToast({
				title: 'Please select Straight or Ramble',
				status: 'warning',
				position: 'top',
			});
			return;
		}
		const isLucky = typeof lucky === 'boolean' ? lucky : isLuckyPick;
		const { selectedNumbers, clearSelectedNumbers } = useBetTwo3DStore.getState();
		if (selectedNumbers.length > 0) {
			let validNumbers = selectedNumbers;
			if (activeBetType) {
				validNumbers = selectedNumbers.filter(
					(num) => !isInvalidRamble(num.toString().padStart(3, '0'), activeGame),
				);
			}

			const newBets = validNumbers.map((num) => ({
				id: `${Date.now()}-${Math.random()}`,
				gameId: gameId,
				gameType: activeGame,
				digit: num.toString().padStart(3, '0'),
				betType: activeBetType ? 'Ramble' : 'Straight',
				betPrice: getMinBetAmount(getGame, gameId) || 1,
				timeName,
				drawId: timeBtnId,
				betTab: activeBetTab,
				odds: getOdds.data?.filter((item) => item.gameId === gameId) || [],
				isLuckyPick: isLucky,
			}));
			if (newBets.length > 0) {
				clearResults();
				setBetting(newBets);
				clearSelectedNumbers();
				setIsLuckyPick(false);
			}
		} else {
			globalShowToast({
				title: 'selection is empty',
				status: 'warning',
				position: 'top',
			});
		}
	};

	const handleBet2EzAdd = (lucky) => {
		if (activeBetType === null) {
			globalShowToast({
				title: 'Please select Straight or Ramble',
				status: 'warning',
				position: 'top',
			});
			return;
		}
		const isLucky = typeof lucky === 'boolean' ? lucky : isLuckyPick;
		const { slots, clearSlots } = useBetTwo2DStore.getState();
		if (slots[0] && slots[1]) {
			const digit = `${slots[0].toString().padStart(2, '0')} ${slots[1].toString().padStart(2, '0')}`;

			if (checkAndToastMax31(digit, activeGame)) return;
			if (checkAndToastInvalidRamble(digit, activeGame)) return;

			const newBet = {
				id: `${Date.now()}-${Math.random()}`,
				gameId: gameId,
				gameType: activeGame,
				digit,
				betType: activeBetType ? 'Ramble' : 'Straight',
				betPrice: getMinBetAmount(getGame, gameId) || 1,
				timeName,
				drawId: timeBtnId,
				betTab: activeBetTab,
				odds: getOdds.data?.filter((item) => item.gameId === gameId) || [],
				isLuckyPick: isLucky,
			};
			if (newBet) {
				clearResults();
				setBetting([newBet]);
				clearSlots();
				setIsLuckyPick(false);
			}
		} else {
			globalShowToast({
				title: 'selection is empty',
				status: 'warning',
				position: 'top',
			});
		}
	};

	const handleBet3Add = (lucky) => {
		if (activeBetType === null) {
			globalShowToast({
				title: 'Please select Straight or Ramble',
				status: 'warning',
				position: 'top',
			});
			return;
		}
		const isLucky = typeof lucky === 'boolean' ? lucky : isLuckyPick;
		const { slots, clearSlots } = useBet3Store.getState();
		const is3D = activeSiteType === 0;
		if (is3D) {
			if (slots.length === 3 && slots.every((s) => s !== '')) {
				const digit = slots.join('');

				if (checkAndToastInvalidRamble(digit, activeGame)) return;

				const newBet = {
					id: `${Date.now()}-${Math.random()}`,
					gameId: gameId,
					gameType: activeGame,
					digit,
					betType: activeBetType ? 'Ramble' : 'Straight',
					betPrice: getMinBetAmount(getGame, gameId) || 1,
					timeName,
					drawId: timeBtnId,
					betTab: activeBetTab,
					odds: getOdds.data?.filter((item) => item.gameId === gameId) || [],
					isLuckyPick: isLucky,
				};
				if (newBet) {
					clearResults();
					setBetting([newBet]);
					clearSlots(3);
					setIsLuckyPick(false);
				}
			}
		} else {
			if (slots.length === 2 && slots.every((s) => s !== '')) {
				const values = slots.map((v) => parseInt(v));
				if (values.every((v) => v >= 1 && v <= 31)) {
					const digit = slots.map((s) => s.toString().padStart(2, '0')).join(' ');

					if (checkAndToastMax31(digit, activeGame)) return;
					if (checkAndToastInvalidRamble(digit, activeGame)) return;

					const newBet = {
						id: `${Date.now()}-${Math.random()}`,
						gameId: gameId,
						gameType: activeGame,
						digit,
						betType: activeBetType ? 'Ramble' : 'Straight',
						betPrice: getMinBetAmount(getGame, gameId) || 1,
						timeName,
						drawId: timeBtnId,
						betTab: activeBetTab,
						odds: getOdds.data?.filter((item) => item.gameId === gameId) || [],
						isLuckyPick: isLucky,
					};
					if (newBet) {
						clearResults();
						setBetting([newBet]);
						clearSlots(2);
						setIsLuckyPick(false);
					}
				} else {
					globalShowToast({
						title: 'Number Input 1 - 31 only.',
						status: 'error',
						position: 'top',
					});
				}
			} else {
				globalShowToast({
					title: 'selection is empty',
					status: 'warning',
					position: 'top',
				});
			}
		}
	};

	const handleAddToList = () => {
		const currentDraw = getDraw.data?.find((d) => d.id === timeBtnId);
		const nowMYT = dayjs(serverTime).tz('Asia/Kuala_Lumpur');
		const drawTimeMYT = currentDraw
			? dayjs.utc(currentDraw.drawTime).tz('Asia/Kuala_Lumpur')
			: null;
		const isDrawExpired = !drawTimeMYT || nowMYT.isAfter(drawTimeMYT) || currentDraw?.cancelled;

		if (time === '' || isDrawExpired) {
			globalShowToast({
				title: 'No valid bets for the current draw time.',
				status: 'warning',
				position: 'top',
			});
			return;
		}

		if (activeBetType === null) {
			globalShowToast({
				title: 'Please select Straight or Ramble',
				status: 'warning',
				position: 'top',
			});
			return;
		}
		if (activeBetTab === 'bet1') {
			if (is3DGame) {
				handleSwer3Add();
			} else if (is2DGame) {
				handleEz2Add();
			}
		} else if (activeBetTab === 'bet2') {
			if (activeSiteType === 0) {
				handleBet2SwerAdd();
			} else if (activeSiteType === 1) {
				handleBet2EzAdd();
			}
		} else if (activeBetTab === 'bet3') {
			handleBet3Add();
		}
	};

	//useEffect
	useEffect(() => {
		const draws = getDraw.data || [];
		const cutoff = getCutOffTime.data?.drawCutoffTime;

		const nowMYT = dayjs(serverTime).tz('Asia/Kuala_Lumpur');

		const anyDrawEnded = draws.some((draw) => {
			if (!draw) return false;

			const endMYT = dayjs.utc(draw.endTime).tz('Asia/Kuala_Lumpur');

			return nowMYT.isSame(endMYT, 'minute');
		});

		const cutoffHit = cutoff
			? nowMYT.isSame(dayjs.utc(cutoff).tz('Asia/Kuala_Lumpur'), 'minute')
			: false;

		if (anyDrawEnded || cutoffHit) {
			if (cutoffHit) {
				getDraw.refetch();
			}
			setActiveTimeBtn(true);
		} else {
			setActiveTimeBtn(false);
		}
	}, [getDraw.data, getDraw.isSuccess, serverTime]);

	useEffect(() => {
		if (serverTime !== null) {
			setHasServerTime(true);
		}
	}, [serverTime]);

	useEffect(() => {
		checkDrawTime();
	}, [activeTimeBtn, hasServerTime, getDraw.isSuccess, getSrvTime.isSuccess, getDraw.data]);

	useEffect(() => {
		clearBettingInput();
	}, [activeBetType]);

	useEffect(() => {
		if (getGame.isSuccess && getGame.data) {
			if (activeSiteType >= getGame.data.length && getGame.data.length > 0) {
				setActiveSiteType(0);
			}
			const game = getGame.data[activeSiteType] || getGame.data[0];
			if (game) {
				setGameId(game.id);
				clearBettingInput();
			}
		}
		if (routeSiteId) {
			setSiteId(routeSiteId);
			setActiveBetType(null);
		}
	}, [getGame.isSuccess, getGame.data, activeSiteType, routeSiteId, setSiteId]);

	useEffect(() => {
		if (getNumberLimit.isSuccess && getNumberLimit.data && activeGame) {
			const betTypeLabel = activeBetType ? 'Ramble' : 'Straight';

			const gameLimitData = getNumberLimit.data?.find((d) => d.gameName === activeGame);
			const drawLimitData = gameLimitData?.draws?.find((d) => (d.id || d.drawId) == timeBtnId);
			const typeLimit = drawLimitData?.list?.find((l) => l.betType === betTypeLabel);

			if (activeBetTab === 'bet2') {
				if (activeSiteType === 0) {
					// 3D/Swer games in Bet 2
					setBet2SwerLimit(typeLimit);
				} else if (activeSiteType === 1) {
					// 2D/EZ games in Bet 2
					setBet2EZLimit(typeLimit);
				}
			}
		}
	}, [
		getNumberLimit.isSuccess,
		getNumberLimit.data,
		activeGame,
		activeBetType,
		timeBtnId,
		activeBetTab,
		activeSiteType,
		setBet2SwerLimit,
		setBet2EZLimit,
	]);

	//props
	const layoutProps = {
		showBetList,
		activeBetTab,
		setActiveBetTab: handleTabChange,
		activeSiteType,
		setActiveSiteType: handleTypeChange,
		getDraw,
		getGame,
		onOpenCheckout: handleOpenCheckout,
		time,
		setTime,
		timeBtnId,
		setTimeBtnId: handleTimeChange,
		timeName,
		setTimeName,
		serverTime,
		activeBetType,
		setActiveBetType,
		handleLucky,
		handleEz2Add,
		setShowBetList,
		swer3Selection,
		ez2Selection,
		handleDigitClick,
		handleAddToList,
		handleBet2SwerAdd,
		handleBet2EzAdd,
		handleBet3Add,
		betting,
		removeBetting,
		removeAllByDigit,
		gameId,
		setIsLuckyPick,
		isDisabled,
		isSelectionIncomplete,
		invalidBetIds,
		setInvalidBetIds,
	};

	return (
		<>
			<BettingDesktopView {...layoutProps} />
			<BettingMobileView {...layoutProps} />

			<GlassModal
				variant='alert'
				title='Confirm to Bet?'
				isOpen={isOpenCheckout}
				isLoading={postPost.isLoading}
				showFooter={!postPost.isLoading}
				leftButton={{ onClick: onCloseCheckout }}
				rightButton={{ onClick: handleSubmit }}
			/>
		</>
	);
};

export default KeyboardScreen;
